// PageKit.js
//
// Shared building blocks for the content pages (About us, Services, Career,
// Privacy, Terms, Health articles, FAQ), in the same language as the landing
// page and the shop:
//
//   - a sand hero panel (the site's off-white with a blush corner, the dot
//     grid and a faint red cross) instead of a centred gradient headline;
//   - white cards and sand panels for content, red kept for accents — dots,
//     numerals, icons, and at most one red card per page;
//   - the landing page's page gutter, so edges line up with the header.
import React from 'react';
import { motion } from 'framer-motion';
import { Eyebrow, PharmacyCross, DOT_GRID_STYLE } from '../Home/landingUi';

export const EASE = [0.22, 1, 0.36, 1];
export const SAND = '#F6F3F0';
export const SAND_GRADIENT = 'linear-gradient(135deg, rgb(var(--brand-tint)) 0%, #F6F3F0 45%, #F6F3F0 100%)';

/** Page frame: gutter + max width. */
export const PageShell = ({ children }) => (
  <div className="min-h-screen bg-white">
    <div className="mx-auto w-full max-w-[1400px] px-[clamp(0.75rem,1.6vw,2rem)] pb-16 pt-5 lg:pb-24">{children}</div>
  </div>
);

/** Decoration for a sand surface. Clipped to `radius`. */
export const SandDecor = ({ radius = 'rounded-[2rem] lg:rounded-[2.5rem]', cross = true, crossClassName }) => (
  <div aria-hidden="true" className={`pointer-events-none absolute inset-0 overflow-hidden ${radius}`}>
    <div className="absolute inset-0" style={DOT_GRID_STYLE} />
    <div
      className="absolute -right-[10%] -top-[40%] h-[140%] w-[55%] rounded-full"
      style={{ background: 'radial-gradient(closest-side, rgb(var(--brand)/0.10), transparent)' }}
    />
    {cross && (
      <PharmacyCross
        className={crossClassName || 'absolute -right-[3%] -top-[25%] h-[150%] w-auto rotate-[-10deg]'}
        style={{ color: 'rgb(var(--brand))', opacity: 0.06 }}
      />
    )}
  </div>
);

/**
 * Sand hero panel.
 * @param eyebrow   small label
 * @param title     h1 — a string, or [lead, highlight] to set the second part in red
 * @param subtitle  lead paragraph
 * @param meta      small line under the subtitle (e.g. "last updated")
 * @param aside     optional right-hand content (image, facts…) on large screens
 */
export const PageHero = ({ eyebrow, title, subtitle, meta, aside, children }) => {
  const [lead, highlight] = Array.isArray(title) ? title : [title, null];
  return (
    <motion.section
      className="relative rounded-[2rem] px-[clamp(1.5rem,4.5vw,4rem)] py-[clamp(2rem,5vw,4rem)] ring-1 ring-black/[0.04] lg:rounded-[2.5rem]"
      style={{ background: SAND_GRADIENT }}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: EASE }}
    >
      <SandDecor />
      <div className={`relative grid grid-cols-1 items-center gap-10 ${aside ? 'lg:grid-cols-12' : ''}`}>
        <div className={aside ? 'lg:col-span-7' : 'max-w-3xl'}>
          {eyebrow && <Eyebrow pulse>{eyebrow}</Eyebrow>}
          <h1 className="mt-5 text-[clamp(2.25rem,5.4vw,4.25rem)] font-extrabold leading-[0.98] tracking-[-0.045em] text-gray-900">
            {lead}
            {highlight && (
              <>
                {' '}
                <span className="text-brand">{highlight}</span>
              </>
            )}
          </h1>
          {subtitle && <p className="mt-5 max-w-2xl text-[clamp(1rem,1.4vw,1.2rem)] leading-relaxed text-gray-500">{subtitle}</p>}
          {meta && (
            <p className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-3.5 py-1.5 text-xs font-medium text-gray-500 ring-1 ring-black/[0.06]">
              {meta}
            </p>
          )}
          {children}
        </div>
        {aside && <div className="lg:col-span-5">{aside}</div>}
      </div>
    </motion.section>
  );
};

/** Section heading: small tracked label + title. */
export const SectionHead = ({ label, title, className = '' }) => (
  <div className={className}>
    {label && <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-400">{label}</p>}
    <h2 className="mt-1.5 text-[clamp(1.5rem,2.4vw,2.1rem)] font-extrabold leading-[1.1] tracking-[-0.035em] text-gray-900">{title}</h2>
  </div>
);

/** White card. `as` lets it render as an article/a. */
export const Card = ({ as: Tag = 'div', className = '', children, ...rest }) => (
  <Tag
    className={`relative rounded-[1.75rem] bg-white p-[clamp(1.5rem,2.6vw,2.25rem)] ring-1 ring-black/[0.05] shadow-[0_18px_40px_-30px_rgba(17,24,39,0.45)] lg:rounded-[2rem] ${className}`}
    {...rest}
  >
    {children}
  </Tag>
);

/** Sand panel with the dot grid (no cross). */
export const SandPanel = ({ className = '', children, ...rest }) => (
  <div
    className={`relative rounded-[1.75rem] p-[clamp(1.5rem,2.6vw,2.25rem)] ring-1 ring-black/[0.04] lg:rounded-[2rem] ${className}`}
    style={{ backgroundColor: SAND }}
    {...rest}
  >
    <SandDecor radius="rounded-[1.75rem] lg:rounded-[2rem]" cross={false} />
    <div className="relative">{children}</div>
  </div>
);

/** Icon in a round disc. tone: 'sand' (default) | 'red' | 'white'. */
export const IconDisc = ({ icon: Icon, tone = 'sand', className = '' }) => {
  const tones = {
    sand: 'bg-[#F6F3F0] text-brand',
    red: 'bg-brand text-white shadow-[0_8px_18px_-8px_rgb(var(--brand)/0.9)]',
    white: 'bg-white text-brand ring-1 ring-black/[0.06]',
  };
  return (
    <span className={`inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl ${tones[tone]} ${className}`} aria-hidden="true">
      <Icon className="h-5 w-5" strokeWidth={1.9} />
    </span>
  );
};

/** Check bullet list. */
export const CheckList = ({ items, className = '', dense = false }) => (
  <ul className={`${dense ? 'space-y-2' : 'space-y-3'} ${className}`}>
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3 text-[15px] leading-relaxed text-gray-600">
        <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand" aria-hidden="true">
          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
          </svg>
        </span>
        <span className="min-w-0">{item}</span>
      </li>
    ))}
  </ul>
);

/** Fade/rise on first view. */
export const Reveal = ({ as = 'div', delay = 0, className = '', children, ...rest }) => {
  const M = motion[as] || motion.div;
  return (
    <M
      className={className}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </M>
  );
};
